import dayjs, { Dayjs } from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import localStorageConstants from "src/app/constants/localStorage.constants";
import {
  AutomaticPayment,
  Payment,
  PaymentPeriod,
  PaymentUser,
  UpcomingPayment,
} from "src/app/models/screens/payments.model";
import {
  cancelAutomaticPayment,
  composePayment,
  getAllPayments,
  getAutomaticPayments,
  getUpcomingPayments,
  searchUsers,
} from "src/app/utils/backendEndpointCalls.util";

interface ParentChild {
  _id: string;
  firstName: string;
}

const PaymentsScreenViewModel = () => {
  const [isSnackbarError, setIsSnackbarError] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [isDisplayingSnackbar, setIsDisplayingSnackbar] =
    useState<boolean>(false);

  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [userRole, setUserRole] = useState<string | undefined>(undefined);

  const [allReceivedPayments, setAllReceivedPayments] = useState<Payment[]>([]);
  const [allSentPayments, setAllSentPayments] = useState<Payment[]>([]);
  const [isLoadingPayments, setIsLoadingPayments] = useState<boolean>(false);
  const [receivedPeriod, setReceivedPeriod] =
    useState<PaymentPeriod>("past_month");
  const [sentPeriod, setSentPeriod] = useState<PaymentPeriod>("past_month");

  const [children, setChildren] = useState<ParentChild[]>([]);
  const [isLoadingChildren, setIsLoadingChildren] = useState<boolean>(false);
  const [adminPaymentUsers, setAdminPaymentUsers] = useState<PaymentUser[]>([]);
  const [isLoadingAdminPaymentUsers, setIsLoadingAdminPaymentUsers] =
    useState<boolean>(false);
  const [selectedOwnerId, setSelectedOwnerId] = useState<string>("you");

  const [automaticPayments, setAutomaticPayments] = useState<
    AutomaticPayment[]
  >([]);
  const [isLoadingAutomaticPayments, setIsLoadingAutomaticPayments] =
    useState<boolean>(false);
  const [upcomingPayments, setUpcomingPayments] = useState<UpcomingPayment[]>(
    [],
  );
  const [isLoadingUpcomingPayments, setIsLoadingUpcomingPayments] =
    useState<boolean>(false);
  const [cancellingAutomaticPaymentId, setCancellingAutomaticPaymentId] =
    useState<string | null>(null);

  const [composeUserSearchText, setComposeUserSearchText] =
    useState<string>("");
  const [isSearchingComposeUsers, setIsSearchingComposeUsers] =
    useState<boolean>(false);
  const [composeSearchedUsers, setComposeSearchedUsers] = useState<
    PaymentUser[]
  >([]);
  const [composeSelectedUser, setComposeSelectedUser] =
    useState<PaymentUser | null>(null);
  const [composeAmount, setComposeAmount] = useState<string>("");
  const [composeDate, setComposeDate] = useState<Dayjs | null>(dayjs());
  const [composeDescription, setComposeDescription] = useState<string>("");
  const [composeIsAutomatic, setComposeIsAutomatic] = useState<boolean>(false);
  const [composeIntervalDays, setComposeIntervalDays] = useState<string>("");
  const [isComposingPayment, setIsComposingPayment] = useState<boolean>(false);

  const showError = (message: string) => {
    setIsSnackbarError(true);
    setSnackbarMessage(message);
    setIsDisplayingSnackbar(true);
  };

  const showSuccess = (message: string) => {
    setIsSnackbarError(false);
    setSnackbarMessage(message);
    setIsDisplayingSnackbar(true);
  };

  const getCurrentChildId = useCallback((): string | undefined => {
    if (userRole !== "Parent") return undefined;
    return selectedOwnerId === "you" ? undefined : selectedOwnerId;
  }, [selectedOwnerId, userRole]);

  const getCurrentAdminUserId = useCallback((): string | undefined => {
    if (userRole !== "Admin") return undefined;
    return selectedOwnerId === "you" ? undefined : selectedOwnerId;
  }, [selectedOwnerId, userRole]);

  const getPeriodStartDate = (period: PaymentPeriod): string | undefined => {
    const today = dayjs();
    if (period === "all_time") return undefined;
    if (period === "past_week")
      return today.subtract(7, "day").format("YYYY-MM-DD");
    if (period === "past_month")
      return today.subtract(1, "month").format("YYYY-MM-DD");
    if (period === "past_quarter")
      return today.subtract(3, "month").format("YYYY-MM-DD");
    if (period === "past_year")
      return today.subtract(1, "year").format("YYYY-MM-DD");
    return undefined;
  };

  const filterPaymentsByPeriod = useCallback(
    (payments: Payment[], period: PaymentPeriod): Payment[] => {
      const startDate = getPeriodStartDate(period);
      if (!startDate) return [...payments];
      return payments.filter((p) => p.date >= startDate);
    },
    [],
  );

  const filteredReceivedPayments = useMemo(
    () => filterPaymentsByPeriod(allReceivedPayments, receivedPeriod),
    [allReceivedPayments, filterPaymentsByPeriod, receivedPeriod],
  );

  const filteredSentPayments = useMemo(
    () => filterPaymentsByPeriod(allSentPayments, sentPeriod),
    [allSentPayments, filterPaymentsByPeriod, sentPeriod],
  );

  const receivedTotal = useMemo(
    () => filteredReceivedPayments.reduce((sum, p) => sum + p.amount, 0),
    [filteredReceivedPayments],
  );

  const sentTotal = useMemo(
    () => filteredSentPayments.reduce((sum, p) => sum + p.amount, 0),
    [filteredSentPayments],
  );

  const buildChartData = useCallback(
    (payments: Payment[], period: PaymentPeriod) => {
      if (payments.length === 0) return [];

      const grouped: Record<string, number> = {};

      payments.forEach((p) => {
        let key: string;
        if (period === "past_week" || period === "past_month") {
          key = p.date;
        } else if (period === "past_quarter") {
          key = dayjs(p.date).format("YYYY-[W]ww");
        } else {
          key = dayjs(p.date).format("YYYY-MM");
        }
        grouped[key] = (grouped[key] ?? 0) + p.amount;
      });

      const startDate = getPeriodStartDate(period) ?? payments[0].date;
      const endDate = dayjs().format("YYYY-MM-DD");

      let current = dayjs(startDate);
      const end = dayjs(endDate);

      while (!current.isAfter(end)) {
        let key: string;
        if (period === "past_week" || period === "past_month") {
          key = current.format("YYYY-MM-DD");
          current = current.add(1, "day");
        } else if (period === "past_quarter") {
          key = current.format("YYYY-[W]ww");
          current = current.add(1, "week");
        } else {
          key = current.format("YYYY-MM");
          current = current.add(1, "month");
        }
        if (!(key in grouped)) {
          grouped[key] = 0;
        }
      }

      return Object.entries(grouped)
        .sort(([a], [b]) => (a < b ? -1 : 1))
        .map(([label, amount]) => ({ label, amount }));
    },
    [],
  );

  const receivedChartData = useMemo(
    () => buildChartData(filteredReceivedPayments, receivedPeriod),
    [buildChartData, filteredReceivedPayments, receivedPeriod],
  );

  const sentChartData = useMemo(
    () => buildChartData(filteredSentPayments, sentPeriod),
    [buildChartData, filteredSentPayments, sentPeriod],
  );

  const formatCurrency = (amount: number): string => `£${amount.toFixed(2)}`;

  const periodLabel = (period: PaymentPeriod): string => {
    if (period === "past_week") return "Past Week";
    if (period === "past_month") return "Past Month";
    if (period === "past_quarter") return "Past Quarter";
    if (period === "past_year") return "Past Year";
    return "All Time";
  };

  const loadAllPayments = useCallback(
    async (childId?: string, userId?: string) => {
      setIsLoadingPayments(true);
      const response = await getAllPayments(childId, userId);
      setIsLoadingPayments(false);
      if (!response.success) {
        showError(response.error);
        return;
      }
      setAllReceivedPayments(response.receivedPayments ?? []);
      setAllSentPayments(response.sentPayments ?? []);
    },
    [],
  );

  const loadAutomaticPayments = useCallback(async () => {
    setIsLoadingAutomaticPayments(true);
    const response = await getAutomaticPayments();
    setIsLoadingAutomaticPayments(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setAutomaticPayments(response.automaticPayments ?? []);
  }, []);

  const loadUpcomingPayments = useCallback(async () => {
    setIsLoadingUpcomingPayments(true);
    const response = await getUpcomingPayments();
    setIsLoadingUpcomingPayments(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    const sorted = [...(response.upcomingPayments ?? [])].sort(
      (a, b) => a.daysUntilDue - b.daysUntilDue,
    );
    setUpcomingPayments(sorted);
  }, []);

  const loadComposeUsers = useCallback(async (query: string) => {
    setIsSearchingComposeUsers(true);
    const response = await searchUsers(query);
    setIsSearchingComposeUsers(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setComposeSearchedUsers(response.users ?? []);
  }, []);

  const loadParentChildren = useCallback(async () => {
    setIsLoadingChildren(true);
    const { getParentChildren } =
      await import("src/app/utils/backendEndpointCalls.util");
    const response = await getParentChildren();
    setIsLoadingChildren(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setChildren(response.children ?? []);
  }, []);

  const loadAdminPaymentUsers = useCallback(async () => {
    setIsLoadingAdminPaymentUsers(true);
    const response = await searchUsers("");
    setIsLoadingAdminPaymentUsers(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setAdminPaymentUsers(response.users ?? []);
  }, []);

  useEffect(() => {
    const savedUserRole: string | null = localStorage.getItem(
      localStorageConstants.ROLE,
    );
    if (!savedUserRole) return;
    setUserRole(savedUserRole);
  }, []);

  useEffect(() => {
    if (!userRole) return;
    if (userRole === "Parent") {
      loadParentChildren();
    } else if (userRole === "Admin") {
      loadAdminPaymentUsers();
    }
  }, [loadAdminPaymentUsers, loadParentChildren, userRole]);

  useEffect(() => {
    if (!userRole) return;
    loadAllPayments(getCurrentChildId(), getCurrentAdminUserId());
  }, [
    userRole,
    selectedOwnerId,
    loadAllPayments,
    getCurrentChildId,
    getCurrentAdminUserId,
  ]);

  useEffect(() => {
    if (userRole !== "Admin") return;
    if (selectedTab === 2) {
      loadAutomaticPayments();
      loadUpcomingPayments();
    }
  }, [userRole, selectedTab, loadAutomaticPayments, loadUpcomingPayments]);

  useEffect(() => {
    if (userRole !== "Admin") return;
    if (selectedTab !== 3) return;
    loadComposeUsers("");
  }, [userRole, selectedTab, loadComposeUsers]);

  useEffect(() => {
    if (userRole !== "Admin") return;
    if (selectedTab !== 3) return;
    const timeout = setTimeout(() => {
      loadComposeUsers(composeUserSearchText.trim());
    }, 300);
    return () => clearTimeout(timeout);
  }, [composeUserSearchText, loadComposeUsers, selectedTab, userRole]);

  const onTabChanged = (_event: React.SyntheticEvent, value: number) => {
    setSelectedTab(value);
  };

  const onSelectedOwnerChanged = (ownerId: string) => {
    setSelectedOwnerId(ownerId);
    setAllReceivedPayments([]);
    setAllSentPayments([]);
  };

  const onReceivedPeriodChanged = (period: PaymentPeriod) => {
    setReceivedPeriod(period);
  };

  const onSentPeriodChanged = (period: PaymentPeriod) => {
    setSentPeriod(period);
  };

  const onCancelAutomaticPayment = async (automaticPaymentId: string) => {
    setCancellingAutomaticPaymentId(automaticPaymentId);
    const response = await cancelAutomaticPayment(automaticPaymentId);
    setCancellingAutomaticPaymentId(null);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setAutomaticPayments((prev) =>
      prev.map((ap) =>
        ap.id === automaticPaymentId ? { ...ap, status: "Cancelled" } : ap,
      ),
    );
    setUpcomingPayments((prev) =>
      prev.filter((up) => up.automaticPaymentId !== automaticPaymentId),
    );
    showSuccess("Automatic payment cancelled successfully.");
  };

  const onComposeUserSelected = (user: PaymentUser) => {
    setComposeSelectedUser(user);
  };

  const onComposeIsAutomaticChanged = (checked: boolean) => {
    setComposeIsAutomatic(checked);
    if (!checked) setComposeIntervalDays("");
  };

  const onSubmitPayment = async () => {
    if (!composeSelectedUser) {
      showError("Please select a recipient.");
      return;
    }
    const parsedAmount = parseFloat(composeAmount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      showError("Please enter a valid amount.");
      return;
    }
    if (!composeDate) {
      showError("Please select a date.");
      return;
    }
    if (composeDate.isAfter(dayjs(), "day")) {
      showError("Payment date cannot be in the future.");
      return;
    }
    if (!composeDescription.trim()) {
      showError("Please provide a description.");
      return;
    }
    if (composeIsAutomatic) {
      const parsedInterval = parseInt(composeIntervalDays, 10);
      if (isNaN(parsedInterval) || parsedInterval <= 0) {
        showError("Please enter a valid interval in days.");
        return;
      }
    }

    setIsComposingPayment(true);
    const response = await composePayment(
      composeSelectedUser.id,
      parsedAmount,
      composeDate.format("YYYY-MM-DD"),
      composeDescription.trim(),
      composeIsAutomatic,
      composeIsAutomatic ? parseInt(composeIntervalDays, 10) : undefined,
    );
    setIsComposingPayment(false);

    if (!response.success) {
      showError(response.error);
      return;
    }

    showSuccess("Payment composed successfully.");
    setComposeSelectedUser(null);
    setComposeAmount("");
    setComposeDate(dayjs());
    setComposeDescription("");
    setComposeIsAutomatic(false);
    setComposeIntervalDays("");

    loadAllPayments(getCurrentChildId(), getCurrentAdminUserId());
    if (composeIsAutomatic) {
      loadAutomaticPayments();
      loadUpcomingPayments();
    }
  };

  return {
    isSnackbarError,
    snackbarMessage,
    isDisplayingSnackbar,
    setIsDisplayingSnackbar,
    selectedTab,
    onTabChanged,
    userRole,
    children,
    isLoadingChildren,
    adminPaymentUsers,
    isLoadingAdminPaymentUsers,
    selectedOwnerId,
    onSelectedOwnerChanged,
    filteredReceivedPayments,
    isLoadingPayments,
    receivedPeriod,
    onReceivedPeriodChanged,
    receivedTotal,
    receivedChartData,
    filteredSentPayments,
    sentPeriod,
    onSentPeriodChanged,
    sentTotal,
    sentChartData,
    automaticPayments,
    isLoadingAutomaticPayments,
    upcomingPayments,
    isLoadingUpcomingPayments,
    onCancelAutomaticPayment,
    cancellingAutomaticPaymentId,
    composeUserSearchText,
    setComposeUserSearchText,
    isSearchingComposeUsers,
    composeSearchedUsers,
    composeSelectedUser,
    onComposeUserSelected,
    composeAmount,
    setComposeAmount,
    composeDate,
    setComposeDate,
    composeDescription,
    setComposeDescription,
    composeIsAutomatic,
    onComposeIsAutomaticChanged,
    composeIntervalDays,
    setComposeIntervalDays,
    isComposingPayment,
    onSubmitPayment,
    formatCurrency,
    periodLabel,
  };
};

export default PaymentsScreenViewModel;
